import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useFamilyKYC, type BeneficialOwner, type FamilyEntity, type BankAccount } from "@/hooks/useFamilyKYC";
import {
  Users,
  Building2,
  Landmark,
  ChevronRight,
  ChevronLeft,
  X,
  Plus,
  Check,
  Loader2,
  FileText,
  MapPin,
  UserCheck,
  Wallet,
} from "lucide-react";

const JURISDICTIONS = [
  { value: "us", label: "United States" },
  { value: "sg", label: "Singapore" },
  { value: "hk", label: "Hong Kong" },
  { value: "uk", label: "United Kingdom" },
  { value: "ch", label: "Switzerland" },
  { value: "ae", label: "UAE" },
  { value: "au", label: "Australia" },
  { value: "jp", label: "Japan" },
  { value: "ky", label: "Cayman Islands" },
  { value: "bvi", label: "British Virgin Islands" },
  { value: "other", label: "Other" },
];

const COUNTRIES = [
  { value: "US", label: "United States" },
  { value: "SG", label: "Singapore" },
  { value: "HK", label: "Hong Kong" },
  { value: "GB", label: "United Kingdom" },
  { value: "CH", label: "Switzerland" },
  { value: "AE", label: "United Arab Emirates" },
  { value: "AU", label: "Australia" },
  { value: "JP", label: "Japan" },
  { value: "CA", label: "Canada" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "OTHER", label: "Other" },
];

const ENTITY_TYPES = [
  { value: "trust", label: "Trust" },
  { value: "spv", label: "SPV" },
  { value: "holding_company", label: "Holding Company" },
  { value: "foundation", label: "Foundation" },
  { value: "partnership", label: "Partnership" },
  { value: "llc", label: "LLC" },
  { value: "other", label: "Other" },
] as const;

const ACCOUNT_TYPES = [
  { value: "checking", label: "Checking" },
  { value: "savings", label: "Savings" },
  { value: "investment", label: "Investment" },
  { value: "custody", label: "Custody" },
] as const;

const NET_WORTH_RANGES = [
  { value: "1-10m", label: "$1M - $10M" },
  { value: "10-50m", label: "$10M - $50M" },
  { value: "50-100m", label: "$50M - $100M" },
  { value: "100-500m", label: "$100M - $500M" },
  { value: "500m-1b", label: "$500M - $1B" },
  { value: "1b+", label: "$1B+" },
];

// Step 1: Profile schema
const profileSchema = z.object({
  legal_name: z.string().min(1, "Legal name is required"),
  formation_date: z.string().optional(),
  jurisdiction: z.string().min(1, "Jurisdiction is required"),
  tax_jurisdiction: z.string().optional(),
  tax_id: z.string().optional(),
});

// Step 2: Address schema
const addressSchema = z.object({
  address_line1: z.string().min(1, "Address is required"),
  address_line2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().min(1, "Country is required"),
  source_of_wealth: z.string().optional(),
  estimated_net_worth: z.string().optional(),
});

// Combined form schema
const formSchema = profileSchema.merge(addressSchema);
type FormData = z.infer<typeof formSchema>;

// Temporary entry types for form state
interface OwnerEntry {
  id: string;
  full_name: string;
  date_of_birth: string;
  nationality: string;
  tax_id: string;
  ownership_percentage: string;
  is_control_person: boolean;
  address_line1: string;
  address_city: string;
  address_country: string;
}

interface EntityEntry {
  id: string;
  entity_type: typeof ENTITY_TYPES[number]["value"];
  name: string;
  jurisdiction: string;
  tax_id: string;
  formation_date: string;
  purpose: string;
}

interface BankAccountEntry {
  id: string;
  bank_name: string;
  account_name: string;
  account_number_last4: string;
  routing_number: string;
  swift_code: string;
  currency: string;
  account_type: typeof ACCOUNT_TYPES[number]["value"] | "";
  is_primary: boolean;
  entity_id: string;
}

const STEP_CONFIG = [
  { label: "Profile", icon: FileText },
  { label: "Address", icon: MapPin },
  { label: "Owners", icon: UserCheck },
  { label: "Entities", icon: Building2 },
  { label: "Accounts", icon: Wallet },
  { label: "Review", icon: Check },
];

interface KYCWizardProps {
  familyId: string;
  orgId: string;
  familyName?: string;
  onComplete?: () => void;
  onCancel?: () => void;
}

export function KYCWizard({
  familyId,
  orgId,
  familyName,
  onComplete,
  onCancel,
}: KYCWizardProps) {
  const { toast } = useToast();
  const [step, setStep] = useState(1);

  // Owner state
  const [owners, setOwners] = useState<OwnerEntry[]>([]);
  const [editingOwner, setEditingOwner] = useState<OwnerEntry | null>(null);

  // Entity state
  const [entities, setEntities] = useState<EntityEntry[]>([]);
  const [editingEntity, setEditingEntity] = useState<EntityEntry | null>(null);

  // Bank account state
  const [bankAccounts, setBankAccounts] = useState<BankAccountEntry[]>([]);
  const [editingAccount, setEditingAccount] = useState<BankAccountEntry | null>(null);

  const {
    kyc,
    owners: existingOwners,
    entities: existingEntities,
    bankAccounts: existingBankAccounts,
    isLoading,
    saveKYCAsync,
    isSavingKYC,
    addOwner,
    deleteOwner,
    addEntity,
    deleteEntity,
    addBankAccount,
    deleteBankAccount,
    completeKYC,
    isCompletingKYC,
  } = useFamilyKYC(familyId, orgId);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      legal_name: "",
      formation_date: "",
      jurisdiction: "",
      tax_jurisdiction: "",
      tax_id: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
      source_of_wealth: "",
      estimated_net_worth: "",
    },
  });

  // Load existing data
  useEffect(() => {
    if (kyc) {
      form.reset({
        legal_name: kyc.legal_name || "",
        formation_date: kyc.formation_date || "",
        jurisdiction: kyc.jurisdiction || "",
        tax_jurisdiction: kyc.tax_jurisdiction || "",
        tax_id: kyc.tax_id || "",
        address_line1: kyc.address_line1 || "",
        address_line2: kyc.address_line2 || "",
        city: kyc.city || "",
        state: kyc.state || "",
        postal_code: kyc.postal_code || "",
        country: kyc.country || "",
        source_of_wealth: kyc.source_of_wealth || "",
        estimated_net_worth: kyc.estimated_net_worth || "",
      });
    }
  }, [kyc, form]);

  // Load existing owners
  useEffect(() => {
    if (existingOwners.length > 0) {
      setOwners(existingOwners.map((o) => ({
        id: o.id,
        full_name: o.full_name,
        date_of_birth: o.date_of_birth || "",
        nationality: o.nationality || "",
        tax_id: o.tax_id || "",
        ownership_percentage: o.ownership_percentage?.toString() || "",
        is_control_person: o.is_control_person,
        address_line1: o.address_line1 || "",
        address_city: o.address_city || "",
        address_country: o.address_country || "",
      })));
    }
  }, [existingOwners]);

  // Load existing entities
  useEffect(() => {
    if (existingEntities.length > 0) {
      setEntities(existingEntities.map((e) => ({
        id: e.id,
        entity_type: e.entity_type,
        name: e.name,
        jurisdiction: e.jurisdiction || "",
        tax_id: e.tax_id || "",
        formation_date: e.formation_date || "",
        purpose: e.purpose || "",
      })));
    }
  }, [existingEntities]);

  // Load existing bank accounts
  useEffect(() => {
    if (existingBankAccounts.length > 0) {
      setBankAccounts(existingBankAccounts.map((b) => ({
        id: b.id,
        bank_name: b.bank_name,
        account_name: b.account_name || "",
        account_number_last4: b.account_number_last4 || "",
        routing_number: b.routing_number || "",
        swift_code: b.swift_code || "",
        currency: b.currency,
        account_type: b.account_type || "",
        is_primary: b.is_primary,
        entity_id: b.entity_id || "",
      })));
    }
  }, [existingBankAccounts]);

  const handleNext = async () => {
    if (step === 1) {
      const isValid = await form.trigger(["legal_name", "jurisdiction", "formation_date", "tax_jurisdiction", "tax_id"]);
      if (isValid) {
        // Save profile data
        const values = form.getValues();
        try {
          await saveKYCAsync({
            legal_name: values.legal_name,
            formation_date: values.formation_date || undefined,
            jurisdiction: values.jurisdiction,
            tax_jurisdiction: values.tax_jurisdiction || undefined,
            tax_id: values.tax_id || undefined,
          });
          setStep(2);
        } catch (error) {
          // Error handled by hook
        }
      }
    } else if (step === 2) {
      const isValid = await form.trigger(["address_line1", "city", "country", "address_line2", "state", "postal_code", "source_of_wealth", "estimated_net_worth"]);
      if (isValid) {
        const values = form.getValues();
        try {
          await saveKYCAsync({
            address_line1: values.address_line1,
            address_line2: values.address_line2 || undefined,
            city: values.city,
            state: values.state || undefined,
            postal_code: values.postal_code || undefined,
            country: values.country,
            source_of_wealth: values.source_of_wealth || undefined,
            estimated_net_worth: values.estimated_net_worth || undefined,
          });
          setStep(3);
        } catch (error) {
          // Error handled by hook
        }
      }
    } else if (step < 6) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  // Owner management
  const createEmptyOwner = (): OwnerEntry => ({
    id: crypto.randomUUID(),
    full_name: "",
    date_of_birth: "",
    nationality: "",
    tax_id: "",
    ownership_percentage: "",
    is_control_person: false,
    address_line1: "",
    address_city: "",
    address_country: "",
  });

  const handleAddOwner = () => {
    setEditingOwner(createEmptyOwner());
  };

  const handleSaveOwner = async () => {
    if (!editingOwner || !editingOwner.full_name) {
      toast({ title: "Full name is required", variant: "destructive" });
      return;
    }

    // Check if this is an existing owner being edited
    const isExisting = existingOwners.some(o => o.id === editingOwner.id);

    if (!isExisting) {
      // Add new owner to database
      addOwner({
        full_name: editingOwner.full_name,
        date_of_birth: editingOwner.date_of_birth || null,
        nationality: editingOwner.nationality || null,
        tax_id: editingOwner.tax_id || null,
        ownership_percentage: editingOwner.ownership_percentage ? parseFloat(editingOwner.ownership_percentage) : null,
        is_control_person: editingOwner.is_control_person,
        address_line1: editingOwner.address_line1 || null,
        address_city: editingOwner.address_city || null,
        address_country: editingOwner.address_country || null,
      });
    }

    // Update local state
    if (owners.some(o => o.id === editingOwner.id)) {
      setOwners(owners.map(o => o.id === editingOwner.id ? editingOwner : o));
    } else {
      setOwners([...owners, editingOwner]);
    }
    setEditingOwner(null);
  };

  const handleRemoveOwner = (id: string) => {
    const isExisting = existingOwners.some(o => o.id === id);
    if (isExisting) {
      deleteOwner(id);
    }
    setOwners(owners.filter(o => o.id !== id));
  };

  // Entity management
  const createEmptyEntity = (): EntityEntry => ({
    id: crypto.randomUUID(),
    entity_type: "trust",
    name: "",
    jurisdiction: "",
    tax_id: "",
    formation_date: "",
    purpose: "",
  });

  const handleAddEntity = () => {
    setEditingEntity(createEmptyEntity());
  };

  const handleSaveEntity = () => {
    if (!editingEntity || !editingEntity.name) {
      toast({ title: "Entity name is required", variant: "destructive" });
      return;
    }

    const isExisting = existingEntities.some(e => e.id === editingEntity.id);

    if (!isExisting) {
      addEntity({
        entity_type: editingEntity.entity_type,
        name: editingEntity.name,
        jurisdiction: editingEntity.jurisdiction || null,
        tax_id: editingEntity.tax_id || null,
        formation_date: editingEntity.formation_date || null,
        purpose: editingEntity.purpose || null,
      });
    }

    if (entities.some(e => e.id === editingEntity.id)) {
      setEntities(entities.map(e => e.id === editingEntity.id ? editingEntity : e));
    } else {
      setEntities([...entities, editingEntity]);
    }
    setEditingEntity(null);
  };

  const handleRemoveEntity = (id: string) => {
    const isExisting = existingEntities.some(e => e.id === id);
    if (isExisting) {
      deleteEntity(id);
    }
    setEntities(entities.filter(e => e.id !== id));
  };

  // Bank account management
  const createEmptyAccount = (): BankAccountEntry => ({
    id: crypto.randomUUID(),
    bank_name: "",
    account_name: "",
    account_number_last4: "",
    routing_number: "",
    swift_code: "",
    currency: "USD",
    account_type: "",
    is_primary: false,
    entity_id: "",
  });

  const handleAddAccount = () => {
    setEditingAccount(createEmptyAccount());
  };

  const handleSaveAccount = () => {
    if (!editingAccount || !editingAccount.bank_name) {
      toast({ title: "Bank name is required", variant: "destructive" });
      return;
    }

    const isExisting = existingBankAccounts.some(b => b.id === editingAccount.id);

    if (!isExisting) {
      addBankAccount({
        bank_name: editingAccount.bank_name,
        account_name: editingAccount.account_name || null,
        account_number_last4: editingAccount.account_number_last4 || null,
        routing_number: editingAccount.routing_number || null,
        swift_code: editingAccount.swift_code || null,
        currency: editingAccount.currency,
        account_type: editingAccount.account_type || null,
        is_primary: editingAccount.is_primary,
        entity_id: editingAccount.entity_id || null,
      });
    }

    if (bankAccounts.some(b => b.id === editingAccount.id)) {
      setBankAccounts(bankAccounts.map(b => b.id === editingAccount.id ? editingAccount : b));
    } else {
      setBankAccounts([...bankAccounts, editingAccount]);
    }
    setEditingAccount(null);
  };

  const handleRemoveAccount = (id: string) => {
    const isExisting = existingBankAccounts.some(b => b.id === id);
    if (isExisting) {
      deleteBankAccount(id);
    }
    setBankAccounts(bankAccounts.filter(b => b.id !== id));
  };

  const handleComplete = () => {
    completeKYC();
    onComplete?.();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          KYC Information
          {familyName && <Badge variant="outline">{familyName}</Badge>}
        </CardTitle>
        <CardDescription>
          {step === 1 && "Enter the family's legal information"}
          {step === 2 && "Provide address and financial details"}
          {step === 3 && "Add beneficial owners (individuals with 25%+ ownership or control)"}
          {step === 4 && "Add entity structures (trusts, SPVs, holding companies)"}
          {step === 5 && "Add bank account information"}
          {step === 6 && "Review all information before submitting"}
        </CardDescription>
      </CardHeader>

      <CardContent>
        {/* Progress indicator */}
        <div className="flex items-center gap-1 mb-6">
          {STEP_CONFIG.map((s, idx) => {
            const Icon = s.icon;
            const stepNum = idx + 1;
            const isActive = stepNum === step;
            const isComplete = stepNum < step;
            return (
              <div key={stepNum} className="flex items-center gap-1 flex-1">
                <div
                  className={`flex items-center justify-center h-8 w-8 rounded-full text-xs font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : isComplete
                      ? "bg-primary/20 text-primary"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {isComplete ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </div>
                {idx < STEP_CONFIG.length - 1 && (
                  <div className={`flex-1 h-1 rounded-full ${stepNum < step ? "bg-primary/40" : "bg-muted"}`} />
                )}
              </div>
            );
          })}
        </div>

        <Form {...form}>
          <form className="space-y-4">
            {/* Step 1: Profile */}
            {step === 1 && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="legal_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Legal Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., The Smith Family Trust" {...field} />
                      </FormControl>
                      <FormDescription>The official legal name of the family office or trust</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="formation_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Formation Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="jurisdiction"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Jurisdiction *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select jurisdiction" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {JURISDICTIONS.map((j) => (
                              <SelectItem key={j.value} value={j.value}>
                                {j.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="tax_jurisdiction"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tax Jurisdiction</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select tax jurisdiction" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {JURISDICTIONS.map((j) => (
                              <SelectItem key={j.value} value={j.value}>
                                {j.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tax_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tax ID / EIN</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., 12-3456789" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Step 2: Address */}
            {step === 2 && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="address_line1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Line 1 *</FormLabel>
                      <FormControl>
                        <Input placeholder="Street address" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address_line2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address Line 2</FormLabel>
                      <FormControl>
                        <Input placeholder="Suite, floor, etc." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City *</FormLabel>
                        <FormControl>
                          <Input placeholder="City" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State / Province</FormLabel>
                        <FormControl>
                          <Input placeholder="State" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="postal_code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Postal Code</FormLabel>
                        <FormControl>
                          <Input placeholder="Postal code" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {COUNTRIES.map((c) => (
                              <SelectItem key={c.value} value={c.value}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="border-t pt-4 mt-6">
                  <h4 className="font-medium mb-4">Financial Information</h4>
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="source_of_wealth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Source of Wealth</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Describe the primary sources of wealth (e.g., business ownership, investments, inheritance)"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="estimated_net_worth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Estimated Net Worth</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select range" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {NET_WORTH_RANGES.map((r) => (
                                <SelectItem key={r.value} value={r.value}>
                                  {r.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Beneficial Owners */}
            {step === 3 && (
              <div className="space-y-4">
                {editingOwner ? (
                  <div className="border rounded-lg p-4 space-y-4">
                    <h4 className="font-medium">
                      {owners.some(o => o.id === editingOwner.id) ? "Edit" : "Add"} Beneficial Owner
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Full Name *</label>
                        <Input
                          value={editingOwner.full_name}
                          onChange={(e) => setEditingOwner({ ...editingOwner, full_name: e.target.value })}
                          placeholder="Full legal name"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Date of Birth</label>
                        <Input
                          type="date"
                          value={editingOwner.date_of_birth}
                          onChange={(e) => setEditingOwner({ ...editingOwner, date_of_birth: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Nationality</label>
                        <Select
                          value={editingOwner.nationality}
                          onValueChange={(v) => setEditingOwner({ ...editingOwner, nationality: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select nationality" />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map((c) => (
                              <SelectItem key={c.value} value={c.value}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Tax ID</label>
                        <Input
                          value={editingOwner.tax_id}
                          onChange={(e) => setEditingOwner({ ...editingOwner, tax_id: e.target.value })}
                          placeholder="SSN or Tax ID"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Ownership %</label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={editingOwner.ownership_percentage}
                          onChange={(e) => setEditingOwner({ ...editingOwner, ownership_percentage: e.target.value })}
                          placeholder="e.g., 25"
                        />
                      </div>
                      <div className="flex items-center space-x-2 pt-6">
                        <Switch
                          checked={editingOwner.is_control_person}
                          onCheckedChange={(v) => setEditingOwner({ ...editingOwner, is_control_person: v })}
                        />
                        <label className="text-sm">Control Person</label>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Address</label>
                        <Input
                          value={editingOwner.address_line1}
                          onChange={(e) => setEditingOwner({ ...editingOwner, address_line1: e.target.value })}
                          placeholder="Street address"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">City</label>
                        <Input
                          value={editingOwner.address_city}
                          onChange={(e) => setEditingOwner({ ...editingOwner, address_city: e.target.value })}
                          placeholder="City"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Country</label>
                        <Select
                          value={editingOwner.address_country}
                          onValueChange={(v) => setEditingOwner({ ...editingOwner, address_country: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Country" />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRIES.map((c) => (
                              <SelectItem key={c.value} value={c.value}>
                                {c.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setEditingOwner(null)}>
                        Cancel
                      </Button>
                      <Button type="button" onClick={handleSaveOwner}>
                        Save Owner
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {owners.length > 0 ? (
                      <div className="space-y-2">
                        {owners.map((owner) => (
                          <div key={owner.id} className="flex items-center justify-between p-3 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <UserCheck className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <span className="font-medium">{owner.full_name}</span>
                                {owner.ownership_percentage && (
                                  <span className="text-sm text-muted-foreground ml-2">
                                    ({owner.ownership_percentage}%)
                                  </span>
                                )}
                              </div>
                              {owner.is_control_person && (
                                <Badge variant="secondary" className="text-xs">Control</Badge>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingOwner(owner)}
                              >
                                Edit
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveOwner(owner.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        No beneficial owners added yet.
                      </div>
                    )}
                    <Button type="button" variant="outline" onClick={handleAddOwner} className="w-full">
                      <Plus className="h-4 w-4" />
                      Add Beneficial Owner
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Step 4: Entities */}
            {step === 4 && (
              <div className="space-y-4">
                {editingEntity ? (
                  <div className="border rounded-lg p-4 space-y-4">
                    <h4 className="font-medium">
                      {entities.some(e => e.id === editingEntity.id) ? "Edit" : "Add"} Entity
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Entity Type *</label>
                        <Select
                          value={editingEntity.entity_type}
                          onValueChange={(v: EntityEntry["entity_type"]) => setEditingEntity({ ...editingEntity, entity_type: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ENTITY_TYPES.map((t) => (
                              <SelectItem key={t.value} value={t.value}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Entity Name *</label>
                        <Input
                          value={editingEntity.name}
                          onChange={(e) => setEditingEntity({ ...editingEntity, name: e.target.value })}
                          placeholder="Entity name"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Jurisdiction</label>
                        <Select
                          value={editingEntity.jurisdiction}
                          onValueChange={(v) => setEditingEntity({ ...editingEntity, jurisdiction: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select jurisdiction" />
                          </SelectTrigger>
                          <SelectContent>
                            {JURISDICTIONS.map((j) => (
                              <SelectItem key={j.value} value={j.value}>
                                {j.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Tax ID</label>
                        <Input
                          value={editingEntity.tax_id}
                          onChange={(e) => setEditingEntity({ ...editingEntity, tax_id: e.target.value })}
                          placeholder="Entity Tax ID"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Formation Date</label>
                        <Input
                          type="date"
                          value={editingEntity.formation_date}
                          onChange={(e) => setEditingEntity({ ...editingEntity, formation_date: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Purpose</label>
                      <Textarea
                        value={editingEntity.purpose}
                        onChange={(e) => setEditingEntity({ ...editingEntity, purpose: e.target.value })}
                        placeholder="Describe the entity's purpose"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setEditingEntity(null)}>
                        Cancel
                      </Button>
                      <Button type="button" onClick={handleSaveEntity}>
                        Save Entity
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {entities.length > 0 ? (
                      <div className="space-y-2">
                        {entities.map((entity) => (
                          <div key={entity.id} className="flex items-center justify-between p-3 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <span className="font-medium">{entity.name}</span>
                                <Badge variant="outline" className="ml-2 text-xs">
                                  {ENTITY_TYPES.find(t => t.value === entity.entity_type)?.label}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingEntity(entity)}
                              >
                                Edit
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveEntity(entity.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        No entities added yet.
                      </div>
                    )}
                    <Button type="button" variant="outline" onClick={handleAddEntity} className="w-full">
                      <Plus className="h-4 w-4" />
                      Add Entity
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Step 5: Bank Accounts */}
            {step === 5 && (
              <div className="space-y-4">
                {editingAccount ? (
                  <div className="border rounded-lg p-4 space-y-4">
                    <h4 className="font-medium">
                      {bankAccounts.some(b => b.id === editingAccount.id) ? "Edit" : "Add"} Bank Account
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Bank Name *</label>
                        <Input
                          value={editingAccount.bank_name}
                          onChange={(e) => setEditingAccount({ ...editingAccount, bank_name: e.target.value })}
                          placeholder="e.g., JPMorgan Chase"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Account Name</label>
                        <Input
                          value={editingAccount.account_name}
                          onChange={(e) => setEditingAccount({ ...editingAccount, account_name: e.target.value })}
                          placeholder="Account holder name"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Account Type</label>
                        <Select
                          value={editingAccount.account_type}
                          onValueChange={(v: BankAccountEntry["account_type"]) => setEditingAccount({ ...editingAccount, account_type: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {ACCOUNT_TYPES.map((t) => (
                              <SelectItem key={t.value} value={t.value}>
                                {t.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Currency</label>
                        <Input
                          value={editingAccount.currency}
                          onChange={(e) => setEditingAccount({ ...editingAccount, currency: e.target.value })}
                          placeholder="USD"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Last 4 Digits</label>
                        <Input
                          value={editingAccount.account_number_last4}
                          onChange={(e) => setEditingAccount({ ...editingAccount, account_number_last4: e.target.value })}
                          maxLength={4}
                          placeholder="1234"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Routing Number</label>
                        <Input
                          value={editingAccount.routing_number}
                          onChange={(e) => setEditingAccount({ ...editingAccount, routing_number: e.target.value })}
                          placeholder="Routing number"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">SWIFT Code</label>
                        <Input
                          value={editingAccount.swift_code}
                          onChange={(e) => setEditingAccount({ ...editingAccount, swift_code: e.target.value })}
                          placeholder="SWIFT/BIC code"
                        />
                      </div>
                    </div>
                    {entities.length > 0 && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Associated Entity</label>
                        <Select
                          value={editingAccount.entity_id}
                          onValueChange={(v) => setEditingAccount({ ...editingAccount, entity_id: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select entity (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">None</SelectItem>
                            {entities.map((e) => (
                              <SelectItem key={e.id} value={e.id}>
                                {e.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={editingAccount.is_primary}
                        onCheckedChange={(v) => setEditingAccount({ ...editingAccount, is_primary: v })}
                      />
                      <label className="text-sm">Primary Account</label>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setEditingAccount(null)}>
                        Cancel
                      </Button>
                      <Button type="button" onClick={handleSaveAccount}>
                        Save Account
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {bankAccounts.length > 0 ? (
                      <div className="space-y-2">
                        {bankAccounts.map((account) => (
                          <div key={account.id} className="flex items-center justify-between p-3 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <Landmark className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <span className="font-medium">{account.bank_name}</span>
                                {account.account_number_last4 && (
                                  <span className="text-sm text-muted-foreground ml-2">
                                    ****{account.account_number_last4}
                                  </span>
                                )}
                              </div>
                              {account.is_primary && (
                                <Badge variant="secondary" className="text-xs">Primary</Badge>
                              )}
                              <Badge variant="outline" className="text-xs">{account.currency}</Badge>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingAccount(account)}
                              >
                                Edit
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveAccount(account.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        No bank accounts added yet.
                      </div>
                    )}
                    <Button type="button" variant="outline" onClick={handleAddAccount} className="w-full">
                      <Plus className="h-4 w-4" />
                      Add Bank Account
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Step 6: Review */}
            {step === 6 && (
              <div className="space-y-6">
                {/* Profile Summary */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium flex items-center gap-2 mb-3">
                    <FileText className="h-4 w-4" />
                    Profile
                  </h4>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <dt className="text-muted-foreground">Legal Name</dt>
                    <dd>{form.getValues("legal_name") || "-"}</dd>
                    <dt className="text-muted-foreground">Jurisdiction</dt>
                    <dd>{JURISDICTIONS.find(j => j.value === form.getValues("jurisdiction"))?.label || "-"}</dd>
                    <dt className="text-muted-foreground">Tax ID</dt>
                    <dd>{form.getValues("tax_id") || "-"}</dd>
                  </dl>
                </div>

                {/* Address Summary */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium flex items-center gap-2 mb-3">
                    <MapPin className="h-4 w-4" />
                    Address
                  </h4>
                  <p className="text-sm">
                    {form.getValues("address_line1")}
                    {form.getValues("address_line2") && <>, {form.getValues("address_line2")}</>}
                    <br />
                    {form.getValues("city")}, {form.getValues("state")} {form.getValues("postal_code")}
                    <br />
                    {COUNTRIES.find(c => c.value === form.getValues("country"))?.label}
                  </p>
                </div>

                {/* Owners Summary */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium flex items-center gap-2 mb-3">
                    <UserCheck className="h-4 w-4" />
                    Beneficial Owners ({owners.length})
                  </h4>
                  {owners.length > 0 ? (
                    <ul className="text-sm space-y-1">
                      {owners.map((o) => (
                        <li key={o.id}>
                          {o.full_name}
                          {o.ownership_percentage && ` (${o.ownership_percentage}%)`}
                          {o.is_control_person && " - Control Person"}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">No beneficial owners added</p>
                  )}
                </div>

                {/* Entities Summary */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium flex items-center gap-2 mb-3">
                    <Building2 className="h-4 w-4" />
                    Entities ({entities.length})
                  </h4>
                  {entities.length > 0 ? (
                    <ul className="text-sm space-y-1">
                      {entities.map((e) => (
                        <li key={e.id}>
                          {e.name} ({ENTITY_TYPES.find(t => t.value === e.entity_type)?.label})
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">No entities added</p>
                  )}
                </div>

                {/* Bank Accounts Summary */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium flex items-center gap-2 mb-3">
                    <Wallet className="h-4 w-4" />
                    Bank Accounts ({bankAccounts.length})
                  </h4>
                  {bankAccounts.length > 0 ? (
                    <ul className="text-sm space-y-1">
                      {bankAccounts.map((b) => (
                        <li key={b.id}>
                          {b.bank_name}
                          {b.account_number_last4 && ` ****${b.account_number_last4}`}
                          {b.is_primary && " (Primary)"}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">No bank accounts added</p>
                  )}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-6 border-t">
              {step > 1 ? (
                <Button type="button" variant="outline" onClick={handleBack}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
              )}

              {step < 6 ? (
                <Button type="button" onClick={handleNext} disabled={isSavingKYC}>
                  {isSavingKYC ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </>
                  )}
                </Button>
              ) : (
                <Button type="button" onClick={handleComplete} disabled={isCompletingKYC}>
                  {isCompletingKYC ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Completing...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Complete KYC
                    </>
                  )}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
